import { getUserAttributes, getUserDisplayName } from '@choco-admin/auth';
import { Card, CardBody } from '@heroui/react';
import React, { useState, useEffect } from 'react';
import {
  FiCoffee,
  FiZap,
  FiTrendingUp,
  FiStar,
  FiUsers,
  FiShoppingBag,
  FiTruck,
  FiShoppingCart,
  FiMessageCircle,
} from 'react-icons/fi';
import { Link } from 'react-router-dom';

const funnyGreetings = [
  'Hey there, superstar! 🌟',
  "Well, well, well... look who's back! 👋",
  'Rise and shine, admin extraordinaire! ☀️',
  'Ready to make magic happen? ✨',
  'Time to admin like a boss! 💼',
  'Welcome back, you magnificent human! 🎉',
  "Let's turn chaos into order! 🎯",
  'Another day, another opportunity to excel! 🚀',
  'Coffee? Check. Admin powers? Activated! ☕',
  "You've got this! (And we've got your back) 💪",
  "Time to show this dashboard who's boss! 👑",
  "Let's make today count! 📊",
  'Welcome to your command center! 🎮',
  'Ready to save the day? 🦸',
  "Let's turn those to-dos into to-dones! ✅",
];

function getRandomGreeting(): string {
  return funnyGreetings[Math.floor(Math.random() * funnyGreetings.length)];
}

const quickLinks = [
  {
    path: '/users',
    label: 'Users',
    icon: FiUsers,
    bgClass: 'bg-primary-100 dark:bg-primary-900/30',
    iconClass: 'text-primary-600 dark:text-primary-400',
  },
  {
    path: '/buyers',
    label: 'Buyers',
    icon: FiShoppingBag,
    bgClass: 'bg-success-100 dark:bg-success-900/30',
    iconClass: 'text-success-600 dark:text-success-400',
  },
  {
    path: '/suppliers',
    label: 'Distributors',
    icon: FiTruck,
    bgClass: 'bg-warning-100 dark:bg-warning-900/30',
    iconClass: 'text-warning-600 dark:text-warning-400',
  },
  {
    path: '/orders',
    label: 'Orders',
    icon: FiShoppingCart,
    bgClass: 'bg-secondary-100 dark:bg-secondary-900/30',
    iconClass: 'text-secondary-600 dark:text-secondary-400',
  },
  {
    path: '/chats',
    label: 'Chats',
    icon: FiMessageCircle,
    bgClass: 'bg-danger-100 dark:bg-danger-900/30',
    iconClass: 'text-danger-600 dark:text-danger-400',
  },
];

export const DashboardPage: React.FC = () => {
  const [userDisplayName, setUserDisplayName] = useState<string | null>(null);
  const [greeting] = useState<string>(getRandomGreeting());
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadUserInfo = async () => {
      try {
        const attributes = await getUserAttributes();
        const displayName = getUserDisplayName(attributes);
        setUserDisplayName(displayName || null);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error loading user info:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadUserInfo();
  }, []);

  const icons = [FiCoffee, FiZap, FiTrendingUp, FiStar];
  const IconComponent = icons[Math.floor(Math.random() * icons.length)];

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="space-y-6">
        {/* Welcome Section */}
        <Card className="bg-gradient-to-br from-content1 to-default-50 border-default-200">
          <CardBody className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary-100 to-primary-200 dark:from-primary-900/30 dark:to-primary-800/30 flex items-center justify-center flex-shrink-0">
                <IconComponent className="h-8 w-8 text-primary-600 dark:text-primary-400" />
              </div>
              <div className="flex-1 min-w-0">
                <h1 className="text-xl font-semibold mb-1 leading-normal tracking-normal">
                  {greeting}
                </h1>
                {!isLoading && userDisplayName && (
                  <p className="text-sm text-default-500">
                    Logged in as{' '}
                    <span className="font-semibold text-default-700 dark:text-default-300">
                      {userDisplayName}
                    </span>
                  </p>
                )}
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Quick Links */}
        <div>
          <h2 className="title-small text-foreground mb-4">Quick Access</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {quickLinks.map((link) => {
              const Icon = link.icon;
              return (
                <Link key={link.path} to={link.path} className="block group">
                  <Card className="h-full hover:shadow-lg transition-all duration-200 hover:scale-[1.02] border-default-200">
                    <CardBody className="p-6 flex flex-col items-center justify-center text-center">
                      <div
                        className={`w-12 h-12 rounded-full ${link.bgClass} flex items-center justify-center mb-3 group-hover:scale-110 transition-transform`}
                      >
                        <Icon className={`h-6 w-6 ${link.iconClass}`} />
                      </div>
                      <p className="font-semibold text-foreground group-hover:text-primary transition-colors">
                        {link.label}
                      </p>
                    </CardBody>
                  </Card>
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};
